/**
 * Phase 2 Integration Tests
 *
 * Automated test functions that verify all Phase 2 systems work correctly:
 * - Cohort system (generation, distribution, data integrity)
 * - Party system (creation, district states, cohort loyalties)
 * - Candidate system (data, constraints, recruitment)
 * - Visualizations (ideology comparison, 2D projection)
 * - Cross-system integration
 */

import { supabase } from '@/lib/supabase'
import {
  validateIdeologyVector,
  validateSalienceWeights,
  validateParty,
  validateCandidate,
  validatePartyLeaderConstraint,
  validatePartyChairConstraint,
  validateDistrictCandidateConstraint,
  VALID_AGE_BRACKETS,
  VALID_EDUCATION_LEVELS,
  VALID_INCOME_LEVELS,
  VALID_RECRUITMENT_SOURCES,
  VALID_CANDIDATE_ROLES,
} from '@/lib/validation'
import {
  calculateRawDistance,
  distanceToSimilarity,
} from '@/lib/ideology'
import {
  project2D,
  getQuadrantInfo,
} from '@/lib/ideology2d'
import type {
  Cohort,
  Party,
  Candidate,
  Nation,
  District,
  IdeologyVector,
} from '@/types'

// ============================================================================
// Types
// ============================================================================

export interface TestResult {
  name: string
  passed: boolean
  details: string
  errors?: string[]
}

export interface Phase2TestSuite {
  results: TestResult[]
  summary: {
    passed: number
    failed: number
    total: number
  }
  duration: number
}

export interface TestCategory {
  name: string
  tests: TestResult[]
  passed: number
  failed: number
}

// ============================================================================
// Test Runner
// ============================================================================

/**
 * Run all Phase 2 tests for a nation.
 */
export async function runPhase2Tests(nationId: string): Promise<Phase2TestSuite> {
  const startTime = Date.now()
  const results: TestResult[] = []

  // Cohort System Tests
  results.push(...(await testCohortSystem(nationId)))

  // Party System Tests
  results.push(...(await testPartySystem(nationId)))

  // Candidate System Tests (for all parties in nation)
  const parties = await fetchPartiesByNation(nationId)
  for (const party of parties) {
    results.push(...(await testCandidateSystem(party.id, party.name)))
  }

  // Visualization Tests
  results.push(...testVisualizationSystem())

  // Integration Tests
  results.push(...(await testIntegration(nationId)))

  const passed = results.filter((r) => r.passed).length
  const failed = results.filter((r) => !r.passed).length

  return {
    results,
    summary: {
      passed,
      failed,
      total: results.length,
    },
    duration: Date.now() - startTime,
  }
}

/**
 * Run tests grouped by category for better UI organization.
 */
export async function runPhase2TestsByCategory(nationId: string): Promise<TestCategory[]> {
  const categories: TestCategory[] = []

  // Cohort System
  const cohortTests = await testCohortSystem(nationId)
  categories.push({
    name: 'Cohort System',
    tests: cohortTests,
    passed: cohortTests.filter((t) => t.passed).length,
    failed: cohortTests.filter((t) => !t.passed).length,
  })

  // Party System
  const partyTests = await testPartySystem(nationId)
  categories.push({
    name: 'Party System',
    tests: partyTests,
    passed: partyTests.filter((t) => t.passed).length,
    failed: partyTests.filter((t) => !t.passed).length,
  })

  // Candidate System
  const parties = await fetchPartiesByNation(nationId)
  const candidateTests: TestResult[] = []
  for (const party of parties) {
    candidateTests.push(...(await testCandidateSystem(party.id, party.name)))
  }
  if (candidateTests.length === 0) {
    candidateTests.push({
      name: 'Candidate System',
      passed: true,
      details: 'No parties exist yet - skipping candidate tests',
    })
  }
  categories.push({
    name: 'Candidate System',
    tests: candidateTests,
    passed: candidateTests.filter((t) => t.passed).length,
    failed: candidateTests.filter((t) => !t.passed).length,
  })

  // Visualizations
  const vizTests = testVisualizationSystem()
  categories.push({
    name: 'Visualizations',
    tests: vizTests,
    passed: vizTests.filter((t) => t.passed).length,
    failed: vizTests.filter((t) => !t.passed).length,
  })

  // Integration
  const integrationTests = await testIntegration(nationId)
  categories.push({
    name: 'Integration',
    tests: integrationTests,
    passed: integrationTests.filter((t) => t.passed).length,
    failed: integrationTests.filter((t) => !t.passed).length,
  })

  return categories
}

// ============================================================================
// Cohort System Tests
// ============================================================================

async function testCohortSystem(nationId: string): Promise<TestResult[]> {
  const results: TestResult[] = []

  // Fetch all cohorts and districts for the nation
  const [cohorts, districts] = await Promise.all([
    fetchCohortsByNation(nationId),
    fetchDistrictsByNation(nationId),
  ])

  // Test: All districts have cohorts
  results.push(testAllDistrictsHaveCohorts(cohorts, districts))

  // Test: All ideology vectors valid
  results.push(testCohortIdeologyVectors(cohorts))

  // Test: All salience weights valid
  results.push(testCohortSalienceWeights(cohorts))

  // Test: Population totals reasonable
  results.push(testCohortPopulations(cohorts, districts))

  // Test: Demographic distribution reasonable
  results.push(testCohortDemographicDistribution(cohorts))

  // Test: Settlement types match districts
  results.push(await testCohortSettlementTypes(cohorts, districts))

  return results
}

function testAllDistrictsHaveCohorts(cohorts: Cohort[], districts: District[]): TestResult {
  const districtsWithCohorts = new Set(cohorts.map((c) => c.district_id))
  const districtsWithoutCohorts = districts.filter((d) => !districtsWithCohorts.has(d.id))

  if (districtsWithoutCohorts.length === 0) {
    return {
      name: 'All districts have cohorts',
      passed: true,
      details: `${districts.length} districts all have cohorts generated`,
    }
  }

  return {
    name: 'All districts have cohorts',
    passed: false,
    details: `${districtsWithoutCohorts.length} districts missing cohorts`,
    errors: districtsWithoutCohorts.map((d) => `District "${d.name}" (${d.id}) has no cohorts`),
  }
}

function testCohortIdeologyVectors(cohorts: Cohort[]): TestResult {
  const errors: string[] = []

  for (const cohort of cohorts) {
    const result = validateIdeologyVector(cohort.ideology_vector)
    if (!result.valid) {
      errors.push(`Cohort ${cohort.id}: ${result.errors.join(', ')}`)
    }
  }

  if (errors.length === 0) {
    return {
      name: 'All ideology vectors valid',
      passed: true,
      details: `${cohorts.length} cohorts all have valid ideology vectors`,
    }
  }

  return {
    name: 'All ideology vectors valid',
    passed: false,
    details: `${errors.length} cohorts have invalid ideology vectors`,
    errors: errors.slice(0, 10), // Limit to first 10 errors
  }
}

function testCohortSalienceWeights(cohorts: Cohort[]): TestResult {
  const errors: string[] = []

  for (const cohort of cohorts) {
    const result = validateSalienceWeights(cohort.salience_weights)
    if (!result.valid) {
      errors.push(`Cohort ${cohort.id}: ${result.errors.join(', ')}`)
    }
  }

  if (errors.length === 0) {
    return {
      name: 'All salience weights valid',
      passed: true,
      details: `${cohorts.length} cohorts all have valid salience weights`,
    }
  }

  return {
    name: 'All salience weights valid',
    passed: false,
    details: `${errors.length} cohorts have invalid salience weights`,
    errors: errors.slice(0, 10),
  }
}

function testCohortPopulations(cohorts: Cohort[], districts: District[]): TestResult {
  const errors: string[] = []

  // Group cohorts by district
  const cohortsByDistrict = new Map<string, Cohort[]>()
  for (const cohort of cohorts) {
    const existing = cohortsByDistrict.get(cohort.district_id) || []
    existing.push(cohort)
    cohortsByDistrict.set(cohort.district_id, existing)
  }

  // Check each district
  for (const district of districts) {
    const districtCohorts = cohortsByDistrict.get(district.id) || []
    const cohortTotal = districtCohorts.reduce((sum, c) => sum + c.population, 0)

    if (districtCohorts.length > 0 && cohortTotal === 0) {
      errors.push(`District "${district.name}": cohorts exist but total population is 0`)
    }

    // Allow some tolerance (up to 10% difference) for rounding
    if (district.population > 0) {
      const diff = Math.abs(cohortTotal - district.population) / district.population
      if (diff > 0.1) {
        errors.push(
          `District "${district.name}": population mismatch - district: ${district.population}, cohorts: ${cohortTotal}`
        )
      }
    }
  }

  // Check for zero or negative populations
  for (const cohort of cohorts) {
    if (cohort.population <= 0) {
      errors.push(`Cohort ${cohort.id} has invalid population: ${cohort.population}`)
    }
  }

  if (errors.length === 0) {
    const totalPop = cohorts.reduce((sum, c) => sum + c.population, 0)
    return {
      name: 'Population totals consistent',
      passed: true,
      details: `Total cohort population: ${totalPop.toLocaleString()} across ${cohorts.length} cohorts`,
    }
  }

  return {
    name: 'Population totals consistent',
    passed: false,
    details: `${errors.length} population issues found`,
    errors: errors.slice(0, 10),
  }
}

function testCohortDemographicDistribution(cohorts: Cohort[]): TestResult {
  if (cohorts.length < 10) {
    return {
      name: 'Demographic distribution reasonable',
      passed: true,
      details: `Only ${cohorts.length} cohorts - distribution check skipped`,
    }
  }

  const warnings: string[] = []

  // Check age bracket distribution
  const ageCounts = new Map<string, number>()
  for (const cohort of cohorts) {
    ageCounts.set(cohort.age_bracket, (ageCounts.get(cohort.age_bracket) || 0) + 1)
  }
  if (ageCounts.size < VALID_AGE_BRACKETS.length) {
    const missing = VALID_AGE_BRACKETS.filter((a) => !ageCounts.has(a))
    warnings.push(`Age brackets missing: ${missing.join(', ')}`)
  }

  // Check education distribution
  const eduCounts = new Map<string, number>()
  for (const cohort of cohorts) {
    eduCounts.set(cohort.education_level, (eduCounts.get(cohort.education_level) || 0) + 1)
  }
  if (eduCounts.size < VALID_EDUCATION_LEVELS.length) {
    const missing = VALID_EDUCATION_LEVELS.filter((e) => !eduCounts.has(e))
    warnings.push(`Education levels missing: ${missing.join(', ')}`)
  }

  // Check income distribution
  const incomeCounts = new Map<string, number>()
  for (const cohort of cohorts) {
    incomeCounts.set(cohort.income_level, (incomeCounts.get(cohort.income_level) || 0) + 1)
  }
  if (incomeCounts.size < VALID_INCOME_LEVELS.length) {
    const missing = VALID_INCOME_LEVELS.filter((i) => !incomeCounts.has(i))
    warnings.push(`Income levels missing: ${missing.join(', ')}`)
  }

  // Check for suspiciously uniform distribution (all cohorts identical)
  const uniqueIdeologies = new Set(cohorts.map((c) => JSON.stringify(c.ideology_vector)))
  if (uniqueIdeologies.size === 1 && cohorts.length > 5) {
    warnings.push('All cohorts have identical ideology vectors')
  }

  if (warnings.length === 0) {
    return {
      name: 'Demographic distribution reasonable',
      passed: true,
      details: `Distribution across ${ageCounts.size} age brackets, ${eduCounts.size} education levels, ${incomeCounts.size} income levels`,
    }
  }

  return {
    name: 'Demographic distribution reasonable',
    passed: warnings.length <= 1, // Allow one warning
    details: `${warnings.length} distribution warning(s) found`,
    errors: warnings,
  }
}

async function testCohortSettlementTypes(
  cohorts: Cohort[],
  districts: District[]
): Promise<TestResult> {
  // This test verifies that cohort distributions are reasonable for their district's settlement type
  // (not that cohorts have settlement types - they inherit from districts)

  const districtMap = new Map(districts.map((d) => [d.id, d]))
  const settlementStats: Record<string, { count: number; avgPopulation: number }> = {}

  for (const cohort of cohorts) {
    const district = districtMap.get(cohort.district_id)
    if (district) {
      const type = district.settlement_type
      if (!settlementStats[type]) {
        settlementStats[type] = { count: 0, avgPopulation: 0 }
      }
      settlementStats[type].count++
      settlementStats[type].avgPopulation += cohort.population
    }
  }

  // Calculate averages
  for (const type of Object.keys(settlementStats)) {
    if (settlementStats[type].count > 0) {
      settlementStats[type].avgPopulation /= settlementStats[type].count
    }
  }

  const details = Object.entries(settlementStats)
    .map(([type, stats]) => `${type}: ${stats.count} cohorts, avg pop ${Math.round(stats.avgPopulation)}`)
    .join('; ')

  return {
    name: 'Settlement type distribution',
    passed: true,
    details: details || 'No cohorts to analyze',
  }
}

// ============================================================================
// Party System Tests
// ============================================================================

async function testPartySystem(nationId: string): Promise<TestResult[]> {
  const results: TestResult[] = []

  const [parties, nation, districts, cohorts] = await Promise.all([
    fetchPartiesByNation(nationId),
    fetchNation(nationId),
    fetchDistrictsByNation(nationId),
    fetchCohortsByNation(nationId),
  ])

  // Test: All parties have valid data
  results.push(testPartyData(parties))

  // Test: Party count within limit
  results.push(testPartyCountLimit(parties, nation))

  // Test: No duplicate party names
  results.push(testNoDuplicatePartyNames(parties))

  // Test: District states initialized
  results.push(await testPartyDistrictStates(parties, districts))

  // Test: Cohort loyalties initialized
  results.push(await testCohortPartyLoyalties(parties, cohorts))

  return results
}

function testPartyData(parties: Party[]): TestResult {
  if (parties.length === 0) {
    return {
      name: 'All parties have valid data',
      passed: true,
      details: 'No parties exist yet',
    }
  }

  const errors: string[] = []

  for (const party of parties) {
    const result = validateParty(party)
    if (!result.valid) {
      errors.push(`Party "${party.name}": ${result.errors.join(', ')}`)
    }

    // Additional checks
    if (!party.color.match(/^#[0-9A-Fa-f]{6}$/)) {
      errors.push(`Party "${party.name}": invalid color format "${party.color}"`)
    }
  }

  if (errors.length === 0) {
    return {
      name: 'All parties have valid data',
      passed: true,
      details: `${parties.length} parties all have valid data`,
    }
  }

  return {
    name: 'All parties have valid data',
    passed: false,
    details: `${errors.length} party validation issues`,
    errors: errors.slice(0, 10),
  }
}

function testPartyCountLimit(parties: Party[], nation: Nation): TestResult {
  const activeParties = parties.filter((p) => p.is_active)

  if (activeParties.length <= nation.max_parties) {
    return {
      name: 'Party count within limit',
      passed: true,
      details: `${activeParties.length}/${nation.max_parties} party slots used`,
    }
  }

  return {
    name: 'Party count within limit',
    passed: false,
    details: `${activeParties.length} active parties exceed max of ${nation.max_parties}`,
    errors: [`Nation has ${activeParties.length} active parties but max_parties is ${nation.max_parties}`],
  }
}

function testNoDuplicatePartyNames(parties: Party[]): TestResult {
  const names = parties.map((p) => p.name.toLowerCase())
  const duplicates = names.filter((name, index) => names.indexOf(name) !== index)

  if (duplicates.length === 0) {
    return {
      name: 'No duplicate party names',
      passed: true,
      details: `All ${parties.length} party names are unique`,
    }
  }

  return {
    name: 'No duplicate party names',
    passed: false,
    details: `${duplicates.length} duplicate party names found`,
    errors: [...new Set(duplicates)].map((name) => `Duplicate party name: "${name}"`),
  }
}

async function testPartyDistrictStates(parties: Party[], districts: District[]): Promise<TestResult> {
  if (parties.length === 0) {
    return {
      name: 'District states initialized',
      passed: true,
      details: 'No parties exist yet',
    }
  }

  const errors: string[] = []

  for (const party of parties) {
    const { data: states, error } = await supabase
      .from('party_district_states')
      .select('*')
      .eq('party_id', party.id)

    if (error) {
      errors.push(`Error fetching states for party "${party.name}": ${error.message}`)
      continue
    }

    if (!states || states.length !== districts.length) {
      errors.push(
        `Party "${party.name}": has ${states?.length || 0} district states, expected ${districts.length}`
      )
    }

    // Check for valid values
    for (const state of states || []) {
      if (state.recognition < 0 || state.recognition > 100) {
        errors.push(`Party "${party.name}": invalid recognition ${state.recognition} in district ${state.district_id}`)
      }
      if (state.organization_strength < 0 || state.organization_strength > 100) {
        errors.push(`Party "${party.name}": invalid org strength ${state.organization_strength} in district ${state.district_id}`)
      }
    }
  }

  if (errors.length === 0) {
    return {
      name: 'District states initialized',
      passed: true,
      details: `All ${parties.length} parties have district states for ${districts.length} districts`,
    }
  }

  return {
    name: 'District states initialized',
    passed: false,
    details: `${errors.length} district state issues`,
    errors: errors.slice(0, 10),
  }
}

async function testCohortPartyLoyalties(parties: Party[], cohorts: Cohort[]): Promise<TestResult> {
  if (parties.length === 0) {
    return {
      name: 'Cohort loyalties initialized',
      passed: true,
      details: 'No parties exist yet',
    }
  }

  if (cohorts.length === 0) {
    return {
      name: 'Cohort loyalties initialized',
      passed: true,
      details: 'No cohorts exist yet',
    }
  }

  const expectedCount = parties.length * cohorts.length
  const errors: string[] = []

  // Count total loyalty records
  const { count, error } = await supabase
    .from('cohort_party_loyalties')
    .select('*', { count: 'exact', head: true })
    .in(
      'party_id',
      parties.map((p) => p.id)
    )

  if (error) {
    return {
      name: 'Cohort loyalties initialized',
      passed: false,
      details: 'Error fetching loyalty records',
      errors: [error.message],
    }
  }

  if (count !== expectedCount) {
    errors.push(
      `Expected ${expectedCount} loyalty records (${parties.length} parties x ${cohorts.length} cohorts), found ${count}`
    )
  }

  // Sample check for valid loyalty scores
  const { data: sampleLoyalties } = await supabase
    .from('cohort_party_loyalties')
    .select('loyalty_score')
    .in(
      'party_id',
      parties.map((p) => p.id)
    )
    .limit(100)

  for (const loyalty of sampleLoyalties || []) {
    if (loyalty.loyalty_score < 0 || loyalty.loyalty_score > 100) {
      errors.push(`Invalid loyalty score: ${loyalty.loyalty_score}`)
    }
  }

  if (errors.length === 0) {
    return {
      name: 'Cohort loyalties initialized',
      passed: true,
      details: `${count} loyalty records for ${parties.length} parties x ${cohorts.length} cohorts`,
    }
  }

  return {
    name: 'Cohort loyalties initialized',
    passed: false,
    details: `${errors.length} loyalty issues`,
    errors: errors.slice(0, 10),
  }
}

// ============================================================================
// Candidate System Tests
// ============================================================================

async function testCandidateSystem(partyId: string, partyName: string): Promise<TestResult[]> {
  const results: TestResult[] = []

  const candidates = await fetchCandidatesByParty(partyId)

  if (candidates.length === 0) {
    return [
      {
        name: `[${partyName}] Candidate data`,
        passed: true,
        details: 'No candidates recruited yet',
      },
    ]
  }

  // Test: All candidates have valid data
  results.push(testCandidateData(candidates, partyName))

  // Test: Role constraints
  results.push(testCandidateRoleConstraints(candidates, partyName))

  // Test: District assignments
  results.push(testCandidateDistrictAssignments(candidates, partyName))

  return results
}

function testCandidateData(candidates: Candidate[], partyName: string): TestResult {
  const errors: string[] = []

  for (const candidate of candidates) {
    const result = validateCandidate(candidate)
    if (!result.valid) {
      errors.push(`"${candidate.name}": ${result.errors.join(', ')}`)
    }

    // Additional checks
    if (!VALID_RECRUITMENT_SOURCES.includes(candidate.recruitment_source)) {
      errors.push(`"${candidate.name}": invalid recruitment source "${candidate.recruitment_source}"`)
    }
    if (!VALID_CANDIDATE_ROLES.includes(candidate.role)) {
      errors.push(`"${candidate.name}": invalid role "${candidate.role}"`)
    }
  }

  if (errors.length === 0) {
    return {
      name: `[${partyName}] Candidate data valid`,
      passed: true,
      details: `${candidates.length} candidates all have valid data`,
    }
  }

  return {
    name: `[${partyName}] Candidate data valid`,
    passed: false,
    details: `${errors.length} candidate validation issues`,
    errors: errors.slice(0, 10),
  }
}

function testCandidateRoleConstraints(candidates: Candidate[], partyName: string): TestResult {
  const errors: string[] = []

  // Test party leader constraint
  const leaderResult = validatePartyLeaderConstraint(candidates)
  if (!leaderResult.valid) {
    errors.push(...leaderResult.errors)
  }

  // Test chair constraint
  const chairResult = validatePartyChairConstraint(candidates)
  if (!chairResult.valid) {
    errors.push(...chairResult.errors)
  }

  // Test district candidate constraint
  const districtResult = validateDistrictCandidateConstraint(candidates)
  if (!districtResult.valid) {
    errors.push(...districtResult.errors)
  }

  if (errors.length === 0) {
    const leaders = candidates.filter((c) => c.role === 'party_leader').length
    const regional = candidates.filter((c) => c.role === 'regional_candidate').length
    return {
      name: `[${partyName}] Role constraints enforced`,
      passed: true,
      details: `${leaders} leader(s), ${regional} regional candidates - constraints satisfied`,
    }
  }

  return {
    name: `[${partyName}] Role constraints enforced`,
    passed: false,
    details: `${errors.length} constraint violations`,
    errors,
  }
}

function testCandidateDistrictAssignments(candidates: Candidate[], partyName: string): TestResult {
  const errors: string[] = []

  for (const candidate of candidates) {
    if (candidate.role === 'regional_candidate') {
      if (!candidate.assigned_district_id) {
        errors.push(`"${candidate.name}": regional candidate without district assignment`)
      }
    } else {
      if (candidate.assigned_district_id) {
        errors.push(`"${candidate.name}": non-regional candidate has district assignment`)
      }
    }
  }

  if (errors.length === 0) {
    const assigned = candidates.filter((c) => c.assigned_district_id).length
    return {
      name: `[${partyName}] District assignments valid`,
      passed: true,
      details: `${assigned} candidates with district assignments`,
    }
  }

  return {
    name: `[${partyName}] District assignments valid`,
    passed: false,
    details: `${errors.length} assignment issues`,
    errors,
  }
}

// ============================================================================
// Visualization System Tests
// ============================================================================

function testVisualizationSystem(): TestResult[] {
  const results: TestResult[] = []

  // Test: Ideology distance calculations
  results.push(testIdeologyDistanceCalculations())

  // Test: 2D projection accuracy
  results.push(test2DProjectionAccuracy())

  // Test: Quadrant labeling
  results.push(testQuadrantLabeling())

  return results
}

function testIdeologyDistanceCalculations(): TestResult {
  const errors: string[] = []

  // Test 1: Distance of identical vectors is 0
  const identical: IdeologyVector = [50, -30, 20, 0, 40, -10, 30]
  const distanceIdentical = calculateRawDistance(identical, identical)
  if (distanceIdentical !== 0) {
    errors.push(`Identical vectors should have distance 0, got ${distanceIdentical}`)
  }

  // Test 2: Distance is symmetric
  const vectorA: IdeologyVector = [50, -30, 20, 0, 40, -10, 30]
  const vectorB: IdeologyVector = [0, 0, 0, 0, 0, 0, 0]
  const distanceAB = calculateRawDistance(vectorA, vectorB)
  const distanceBA = calculateRawDistance(vectorB, vectorA)
  if (Math.abs(distanceAB - distanceBA) > 0.001) {
    errors.push(`Distance should be symmetric: (A,B)=${distanceAB}, (B,A)=${distanceBA}`)
  }

  // Test 3: Maximum distance is calculated correctly
  const maxLeft: IdeologyVector = [-100, -100, -100, -100, -100, -100, -100]
  const maxRight: IdeologyVector = [100, 100, 100, 100, 100, 100, 100]
  const maxDistance = calculateRawDistance(maxLeft, maxRight)
  const expectedMax = Math.sqrt(7 * Math.pow(200, 2)) // ~529.15
  if (Math.abs(maxDistance - expectedMax) > 1) {
    errors.push(`Max distance should be ~${expectedMax.toFixed(2)}, got ${maxDistance.toFixed(2)}`)
  }

  // Test 4: Similarity conversion
  const similarity = distanceToSimilarity(0)
  if (similarity !== 100) {
    errors.push(`Zero distance should give 100% similarity, got ${similarity}`)
  }

  if (errors.length === 0) {
    return {
      name: 'Ideology distance calculations',
      passed: true,
      details: 'Distance calculations, symmetry, and similarity conversions all correct',
    }
  }

  return {
    name: 'Ideology distance calculations',
    passed: false,
    details: `${errors.length} calculation errors`,
    errors,
  }
}

function test2DProjectionAccuracy(): TestResult {
  const errors: string[] = []

  // Test archetype positions based on the formula:
  // Horizontal: economic(-0.30), social(-0.25), national(-0.20), environment(-0.15), foreign(+0.10)
  // Vertical: gov_centralization(+0.55), civil_liberties(-0.25), foreign(+0.12), economic(+0.08)

  // State Populist: high economic regulation (+), progressive social (+), high centralization (+)
  // Expected: Left (high regulation = left), Centralized (high gov_cent = top)
  const statePopulist: IdeologyVector = [80, 60, -40, 40, 50, 40, 80]
  const spCoords = project2D(statePopulist)
  if (spCoords.x > 0) {
    errors.push(`State Populist should be left (x<0), got x=${spCoords.x}`)
  }
  if (spCoords.y < 0) {
    errors.push(`State Populist should be centralized (y>0), got y=${spCoords.y}`)
  }

  // Market Liberties: low regulation (-), traditional social (-), low centralization (-)
  // Expected: Right (low regulation = right), Pluralist (low centralization = bottom)
  const marketLib: IdeologyVector = [-80, -60, 80, -60, -50, -40, -80]
  const mlCoords = project2D(marketLib)
  if (mlCoords.x < 0) {
    errors.push(`Market Liberties should be right (x>0), got x=${mlCoords.x}`)
  }
  if (mlCoords.y > 0) {
    errors.push(`Market Liberties should be pluralist (y<0), got y=${mlCoords.y}`)
  }

  // Centrist: all zeros
  // Expected: Center (x~0, y~0)
  const centrist: IdeologyVector = [0, 0, 0, 0, 0, 0, 0]
  const cCoords = project2D(centrist)
  if (Math.abs(cCoords.x) > 10) {
    errors.push(`Centrist should be center (x~0), got x=${cCoords.x}`)
  }
  if (Math.abs(cCoords.y) > 10) {
    errors.push(`Centrist should be center (y~0), got y=${cCoords.y}`)
  }

  if (errors.length === 0) {
    return {
      name: '2D projection accuracy',
      passed: true,
      details: 'Archetype positions project correctly (State Populist top-left, Market Liberties bottom-right, Centrist center)',
    }
  }

  return {
    name: '2D projection accuracy',
    passed: false,
    details: `${errors.length} projection issues`,
    errors,
  }
}

function testQuadrantLabeling(): TestResult {
  const errors: string[] = []

  // Test that all 9 quadrants can be reached
  const testVectors: Array<{ name: string; vector: IdeologyVector; expectedQuadrant: string }> = [
    // Top row (Centralized)
    { name: 'Top-Left', vector: [80, 80, -80, 80, 80, 40, 80], expectedQuadrant: 'State Populists' },
    { name: 'Top-Center', vector: [0, 0, -30, 0, 0, 30, 70], expectedQuadrant: 'Managed Democrats' },
    { name: 'Top-Right', vector: [-80, -80, -80, -80, -80, 60, 80], expectedQuadrant: 'National Conservatives' },
    // Middle row (Institutional)
    { name: 'Mid-Left', vector: [60, 60, 0, 40, 40, 0, 0], expectedQuadrant: 'Social Democrats' },
    { name: 'Mid-Center', vector: [0, 0, 0, 0, 0, 0, 0], expectedQuadrant: 'Pluralist Centrists' },
    { name: 'Mid-Right', vector: [-60, -40, 0, -40, -40, 0, 0], expectedQuadrant: 'Liberal Conservatives' },
    // Bottom row (Pluralist)
    { name: 'Bottom-Left', vector: [60, 60, 80, 40, 40, -40, -80], expectedQuadrant: 'Participatory Progressives' },
    { name: 'Bottom-Center', vector: [0, 0, 80, 0, 0, -40, -70], expectedQuadrant: 'Civil Libertarians' },
    { name: 'Bottom-Right', vector: [-80, -60, 80, -60, -60, -40, -80], expectedQuadrant: 'Market Liberties' },
  ]

  for (const test of testVectors) {
    const info = getQuadrantInfo(test.vector)
    if (info.quadrantName !== test.expectedQuadrant) {
      errors.push(`${test.name}: expected "${test.expectedQuadrant}", got "${info.quadrantName}"`)
    }
  }

  // Test that sub-labels are non-empty
  const sampleVector: IdeologyVector = [50, 30, -20, 20, 30, 10, 40]
  const sampleInfo = getQuadrantInfo(sampleVector)
  if (!sampleInfo.subLabel || sampleInfo.subLabel.length === 0) {
    errors.push('Sub-label should not be empty')
  }

  if (errors.length === 0) {
    return {
      name: 'Quadrant labeling',
      passed: true,
      details: 'All 9 quadrants reachable with correct labels',
    }
  }

  return {
    name: 'Quadrant labeling',
    passed: false,
    details: `${errors.length} labeling issues`,
    errors,
  }
}

// ============================================================================
// Integration Tests
// ============================================================================

async function testIntegration(nationId: string): Promise<TestResult[]> {
  const results: TestResult[] = []

  // Test: Data consistency check
  results.push(await testDataConsistency(nationId))

  // Test: Cross-reference integrity
  results.push(await testCrossReferenceIntegrity(nationId))

  return results
}

async function testDataConsistency(nationId: string): Promise<TestResult> {
  const errors: string[] = []

  // Check for cohorts with null district_id (should never happen with FK constraints)
  const { data: cohortsWithNullDistrict } = await supabase
    .from('cohorts')
    .select('id, district_id')
    .is('district_id', null)

  if (cohortsWithNullDistrict && cohortsWithNullDistrict.length > 0) {
    errors.push(`${cohortsWithNullDistrict.length} cohorts with null district_id found`)
  }

  // Verify loyalty record counts make sense
  const parties = await fetchPartiesByNation(nationId)
  const cohortsList = await fetchCohortsByNation(nationId)

  if (parties.length > 0 && cohortsList.length > 0) {
    const { count } = await supabase
      .from('cohort_party_loyalties')
      .select('*', { count: 'exact', head: true })
      .in(
        'party_id',
        parties.map((p) => p.id)
      )

    const expected = parties.length * cohortsList.length
    if (count !== expected) {
      errors.push(`Loyalty record count mismatch: expected ${expected}, found ${count}`)
    }
  }

  if (errors.length === 0) {
    return {
      name: 'Data consistency check',
      passed: true,
      details: 'No orphaned records or integrity issues found',
    }
  }

  return {
    name: 'Data consistency check',
    passed: false,
    details: `${errors.length} consistency issues`,
    errors,
  }
}

async function testCrossReferenceIntegrity(nationId: string): Promise<TestResult> {
  const errors: string[] = []

  // Verify nation exists
  const { data: nation, error: nationError } = await supabase
    .from('nations')
    .select('id, name, world_id')
    .eq('id', nationId)
    .single()

  if (nationError || !nation) {
    return {
      name: 'Cross-reference integrity',
      passed: false,
      details: 'Nation not found',
      errors: [nationError?.message || 'Nation does not exist'],
    }
  }

  // Verify world exists
  const { data: world, error: worldError } = await supabase
    .from('worlds')
    .select('id')
    .eq('id', nation.world_id)
    .single()

  if (worldError || !world) {
    errors.push(`Nation's world (${nation.world_id}) not found`)
  }

  // Verify all regions belong to this nation
  const { data: regions } = await supabase.from('regions').select('id, nation_id').eq('nation_id', nationId)

  // Verify districts exist for regions
  const regionIds = (regions || []).map((r) => r.id)
  if (regionIds.length > 0) {
    const { count: districtCount } = await supabase
      .from('districts')
      .select('*', { count: 'exact', head: true })
      .in('region_id', regionIds)

    if (districtCount === 0 && regionIds.length > 0) {
      errors.push('Regions exist but no districts found')
    }
  }

  // Verify all parties belong to this nation
  const { data: parties } = await supabase
    .from('parties')
    .select('id, nation_id')
    .eq('nation_id', nationId)

  for (const party of parties || []) {
    if (party.nation_id !== nationId) {
      errors.push(`Party ${party.id} has incorrect nation_id`)
    }
  }

  if (errors.length === 0) {
    return {
      name: 'Cross-reference integrity',
      passed: true,
      details: `Nation "${nation.name}" - all entity references are valid`,
    }
  }

  return {
    name: 'Cross-reference integrity',
    passed: false,
    details: `${errors.length} integrity issues`,
    errors,
  }
}

// ============================================================================
// Helper Functions
// ============================================================================

async function fetchCohortsByNation(nationId: string): Promise<Cohort[]> {
  const { data, error } = await supabase
    .from('cohorts')
    .select('*, district:districts!inner(region:regions!inner(nation_id))')
    .eq('district.region.nation_id', nationId)

  if (error) throw error
  return data as Cohort[]
}

async function fetchDistrictsByNation(nationId: string): Promise<District[]> {
  const { data, error } = await supabase
    .from('districts')
    .select('*, region:regions!inner(nation_id)')
    .eq('region.nation_id', nationId)

  if (error) throw error
  return data as District[]
}

async function fetchPartiesByNation(nationId: string): Promise<Party[]> {
  const { data, error } = await supabase
    .from('parties')
    .select('*')
    .eq('nation_id', nationId)
    .eq('is_active', true)

  if (error) throw error
  return data as Party[]
}

async function fetchNation(nationId: string): Promise<Nation> {
  const { data, error } = await supabase.from('nations').select('*').eq('id', nationId).single()

  if (error) throw error
  return data as Nation
}

async function fetchCandidatesByParty(partyId: string): Promise<Candidate[]> {
  const { data, error } = await supabase
    .from('candidates')
    .select('*')
    .eq('party_id', partyId)
    .eq('is_active', true)

  if (error) throw error
  return data as Candidate[]
}

// ============================================================================
// Export test functions for individual category testing
// ============================================================================

export {
  testCohortSystem,
  testPartySystem,
  testCandidateSystem,
  testVisualizationSystem,
  testIntegration,
}
